import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { faqData } from '../../lib/seoConfig';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  index: number;
  isVisible: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({ 
  question, 
  answer, 
  isOpen, 
  onClick, 
  index, 
  isVisible 
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, answer]);

  return (
    <div 
      className={`border-b border-border-light dark:border-border-dark transition-all duration-500 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 0.1 + 0.3}s` }}
    >
      <button
        className="flex items-center justify-between w-full py-5 text-left focus:outline-none"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark pr-8">
          {question}
        </h3>
        <span className="flex-shrink-0 ml-4">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-primary-light dark:text-primary-dark" />
          ) : (
            <ChevronDown className="h-5 w-5 text-text-secondary-light dark:text-text-secondary-dark" />
          )}
        </span>
      </button>
      <div 
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: `${height}px` }}
      >
        <div className="pb-5 text-base text-text-secondary-light dark:text-text-secondary-dark">
          {answer}
        </div>
      </div>
    </div>
  );
};

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First item open by default
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} id="faq" className="py-32 relative overflow-hidden">
      {/* Background Effects similar to other sections */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-700" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-700 delay-150" 
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      <div className="container mx-auto px-4 relative">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-12 transform transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
              Everything you need to know about MockTrail's AI interview coaching platform
            </p>
          </div>
          
          <div className="space-y-0 divide-y divide-border-light dark:divide-border-dark">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => toggleFAQ(index)}
                index={index}
                isVisible={isVisible}
              />
            ))}
          </div>

          <div className={`text-center mt-12 transform transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`} style={{ transitionDelay: '0.8s' }}>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Still have questions? We'll answer!
            </p>
            <a 
              href="mailto:support@mocktrail.app" 
              className="btn-primary inline-flex items-center gap-2 py-4"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}